import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PathAnalysis = ({ selectedPath, onPathSelect, onExportPath }) => {
  const [expandedPath, setExpandedPath] = useState(null);

  // Mock path analysis data
  const analysisData = {
    totalPaths: 12,
    trackedAmount: 15.75,
    recoveredAmount: 8.25,
    confidenceScore: 0.78,
    paths: [
      {
        id: 'path_1',
        confidence: 0.95,
        amount: 8.25,
        hops: 4,
        pattern: 'direct_transfer',
        risk: 'high',
        addresses: [
          {
            address: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
            type: 'source',
            amount: 8.25,
            timestamp: '2024-09-14T10:30:00Z'
          },
          {
            address: '3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy',
            type: 'intermediate',
            amount: 8.25,
            timestamp: '2024-09-14T10:32:00Z'
          },
          {
            address: 'mixer_service_001',
            type: 'mixer',
            amount: 8.25,
            timestamp: '2024-09-14T11:15:00Z'
          },
          {
            address: '1BvBMSEYstWetqTFn5Au4m4GFg7xJaNVN2',
            type: 'destination',
            amount: 5.75,
            timestamp: '2024-09-14T12:45:00Z'
          }
        ]
      },
      {
        id: 'path_2',
        confidence: 0.88,
        amount: 7.50,
        hops: 3,
        pattern: 'exchange_deposit',
        risk: 'medium',
        addresses: [
          {
            address: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
            type: 'source',
            amount: 7.50,
            timestamp: '2024-09-14T10:30:00Z'
          },
          {
            address: 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
            type: 'intermediate',
            amount: 7.50,
            timestamp: '2024-09-14T10:35:00Z'
          },
          {
            address: 'binance_hot_wallet_1',
            type: 'exchange',
            amount: 7.50,
            timestamp: '2024-09-14T11:20:00Z'
          }
        ]
      },
      {
        id: 'path_3',
        confidence: 0.65,
        amount: 2.50,
        hops: 5,
        pattern: 'peel_chain',
        risk: 'high',
        addresses: [
          {
            address: 'mixer_service_001',
            type: 'mixer',
            amount: 2.50,
            timestamp: '2024-09-14T12:47:00Z'
          },
          {
            address: '3FupnqMDaQjLcGsrf6kEjSWMrsBqEoJhCx',
            type: 'intermediate',
            amount: 2.50,
            timestamp: '2024-09-14T13:15:00Z'
          },
          {
            address: '1K2SXgApmo9uzac8ZfUnVf3MRf2F399Gjy',
            type: 'intermediate',
            amount: 2.30,
            timestamp: '2024-09-14T13:45:00Z'
          },
          {
            address: '3CMNFxN1oHBc4R9EpP2Q1pDw2BcpEf5Zaw',
            type: 'intermediate',
            amount: 2.10,
            timestamp: '2024-09-14T14:20:00Z'
          },
          {
            address: '1F1tAaz5x1HUXrCNLbtMDqcw6o5GNn4xqX',
            type: 'destination',
            amount: 1.95,
            timestamp: '2024-09-14T15:00:00Z'
          }
        ]
      }
    ]
  };

  const getPatternIcon = (pattern) => {
    switch (pattern) {
      case 'direct_transfer': return 'ArrowRight';
      case 'exchange_deposit': return 'Building2';
      case 'peel_chain': return 'GitBranch';
      case 'mixing_service': return 'Shuffle';
      default: return 'HelpCircle';
    }
  };

  const getPatternLabel = (pattern) => {
    switch (pattern) {
      case 'direct_transfer': return 'Direct Transfer';
      case 'exchange_deposit': return 'Exchange Deposit';
      case 'peel_chain': return 'Peel Chain';
      case 'mixing_service': return 'Mixing Service';
      default: return 'Unknown Pattern';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-amber-600 bg-amber-50';
      case 'low': return 'text-emerald-600 bg-emerald-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString();
  };

  const handlePathToggle = (pathId) => {
    setExpandedPath(expandedPath === pathId ? null : pathId);
  };

  const handlePathSelect = (path) => {
    onPathSelect?.(path);
  };

  const handleExportPath = (path) => {
    onExportPath?.(path);
  };

  return (
    <div className="bg-surface rounded-lg border border-border shadow-forensic-card">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Route" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-text-primary">Path Analysis</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExportPath?.(analysisData)}
          >
            <Icon name="Download" size={16} className="mr-2" />
            Export All
          </Button>
        </div>
        
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">
              {analysisData?.totalPaths}
            </div>
            <div className="text-sm text-text-secondary">Total Paths</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">
              {analysisData?.trackedAmount}
            </div>
            <div className="text-sm text-text-secondary">BTC Tracked</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-600">
              {analysisData?.recoveredAmount}
            </div>
            <div className="text-sm text-text-secondary">BTC Recovered</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600">
              {Math.round(analysisData?.confidenceScore * 100)}%
            </div>
            <div className="text-sm text-text-secondary">Avg Confidence</div>
          </div>
        </div>
      </div>
      {/* Path List */}
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {analysisData?.paths?.map((path) => (
          <div key={path?.id} className="p-4">
            {/* Path Summary */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePathToggle(path?.id)}
                >
                  <Icon 
                    name={expandedPath === path?.id ? "ChevronDown" : "ChevronRight"} 
                    size={16} 
                  />
                </Button>
                
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={getPatternIcon(path?.pattern)} 
                    size={16} 
                    className="text-primary" 
                  />
                  <span className="font-medium text-text-primary">
                    {getPatternLabel(path?.pattern)}
                  </span>
                </div>
                
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(path?.risk)}`}>
                  {path?.risk} risk
                </span>
              </div>
              
              <div className="flex items-center space-x-4 text-sm text-text-secondary">
                <div className="flex items-center space-x-1">
                  <Icon name="Coins" size={14} />
                  <span>{path?.amount} BTC</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="GitCommit" size={14} />
                  <span>{path?.hops} hops</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="TrendingUp" size={14} />
                  <span>{Math.round(path?.confidence * 100)}%</span>
                </div>
                
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePathSelect(path)}
                  >
                    <Icon name="Eye" size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleExportPath(path)}
                  >
                    <Icon name="Download" size={14} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Expanded Path Details */}
            {expandedPath === path?.id && (
              <div className="mt-4 ml-8">
                <div className="space-y-3">
                  {path?.addresses?.map((address, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      {/* Step Indicator */}
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                          address?.type === 'source' ? 'bg-red-100 text-red-800' :
                          address?.type === 'destination' ? 'bg-blue-100 text-blue-800' :
                          address?.type === 'mixer' ? 'bg-purple-100 text-purple-800' :
                          address?.type === 'exchange'? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {index + 1}
                        </div>
                        {index < path?.addresses?.length - 1 && (
                          <div className="w-px h-6 bg-border mt-1"></div>
                        )}
                      </div>
                      
                      {/* Address Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="font-mono text-sm text-text-primary truncate">
                              {address?.address}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              address?.type === 'source' ? 'bg-red-100 text-red-800' :
                              address?.type === 'destination' ? 'bg-blue-100 text-blue-800' :
                              address?.type === 'mixer' ? 'bg-purple-100 text-purple-800' :
                              address?.type === 'exchange'? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {address?.type}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-text-secondary">
                            <span>{address?.amount} BTC</span>
                            <span>{formatTimestamp(address?.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PathAnalysis;